import * as React from 'react';
import { cn } from '@utils';

const ICON_SIZE = 16;
const ICON_SIZE_LARGE = 24;
const DRAG_HANDLE_DOT_RADIUS = 1.5;
const DRAG_HANDLE_DOT_X_LEFT = 4;
const DRAG_HANDLE_DOT_X_RIGHT = 12;
const DRAG_HANDLE_DOT_Y_TOP = 4;
const DRAG_HANDLE_DOT_Y_MIDDLE = 8;
const DRAG_HANDLE_DOT_Y_BOTTOM = 12;
const ICON_STROKE_WIDTH = 2;
const MIN_ITEM_HEIGHT = 48;
const DISABLED_OPACITY = 0.6;
const DRAGGING_OPACITY = 0.5;
const DISABLED_BUTTON_OPACITY = 0.3;

const ARROW_UP_POINTS = '18 15 12 9 6 15';
const ARROW_DOWN_POINTS = '6 9 12 15 18 9';
const DELETE_LINE_POINTS = '3 6 5 6 21 6';
const DELETE_PATH_D = 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2';

/**
 * Props for DraggableListItem component
 *
 * @property id - Unique identifier for the item
 * @property index - Index of the item in the list
 * @property isDragging - Whether the item is currently being dragged
 * @property isDragOver - Whether drag-over effect should be shown
 * @property isDisabled - Whether drag functionality is disabled
 * @property showArrows - Whether to show up/down arrow buttons
 * @property showDelete - Whether to show delete button
 * @property children - Content to display in the item
 * @property onDragStart - Callback when drag starts
 * @property onDragEnd - Callback when drag ends
 * @property onDragOver - Callback when dragged over
 * @property onDragLeave - Callback when drag leaves
 * @property onDrop - Callback when dropped
 * @property onMoveUp - Callback when move up is clicked
 * @property onMoveDown - Callback when move down is clicked
 * @property onDelete - Callback when delete is clicked
 * @property canMoveUp - Whether move up button should be disabled
 * @property canMoveDown - Whether move down button should be disabled
 * @property className - Custom className for styling
 * @property dragIcon - Drag handle icon component
 * @property deleteIcon - Delete icon component
 * @property moveUpIcon - Move up icon component
 * @property moveDownIcon - Move down icon component
 */
export interface IDraggableListItemProps {
	id: string | number;
	index: number;
	isDragging: boolean;
	isDragOver: boolean;
	isDisabled?: boolean;
	showArrows?: boolean;
	showDelete?: boolean;
	children: React.ReactNode;
	onDragStart: () => void;
	onDragEnd: () => void;
	onDragOver: (e: React.DragEvent) => void;
	onDragLeave: () => void;
	onDrop: () => void;
	onMoveUp?: () => void;
	onMoveDown?: () => void;
	onDelete?: () => void;
	canMoveUp?: boolean;
	canMoveDown?: boolean;
	className?: string;
	dragIcon?: React.ReactNode;
	deleteIcon?: React.ReactNode;
	moveUpIcon?: React.ReactNode;
	moveDownIcon?: React.ReactNode;
}

export const DraggableListItem: React.FC<IDraggableListItemProps> = ({
	id,
	index,
	isDragging,
	isDragOver,
	isDisabled = false,
	showArrows = true,
	showDelete = true,
	children,
	onDragStart,
	onDragEnd,
	onDragOver,
	onDragLeave,
	onDrop,
	onMoveUp,
	onMoveDown,
	onDelete,
	canMoveUp = true,
	canMoveDown = true,
	className,
	dragIcon,
	deleteIcon,
	moveUpIcon,
	moveDownIcon,
}) => {
	return (
		<div
			draggable={!isDisabled}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			onDragOver={isDisabled ? undefined : onDragOver}
			onDragLeave={isDisabled ? undefined : onDragLeave}
			onDrop={isDisabled ? undefined : onDrop}
			className={cn(
				'group flex items-center justify-between',
				'px-4 py-3 bg-white border border-borderSeparator rounded-lg',
				`min-h-[${MIN_ITEM_HEIGHT}px] transition-all duration-200`,
				!isDisabled && 'cursor-grab active:cursor-grabbing',
				isDisabled && 'cursor-default',
				isDragOver && 'border-primary border-2 bg-bgSecondary',
				'hover:bg-bgSecondary hover:border-borderPrimary',
				className
			)}
			style={{
				opacity: isDisabled ? DISABLED_OPACITY : isDragging ? DRAGGING_OPACITY : undefined,
			}}
			data-item-id={id}
			data-item-index={index}
		>
			<div className="flex items-center gap-3 flex-1 min-w-0">
				{!isDisabled && (
					<div className="shrink-0 text-tertiary cursor-grab active:cursor-grabbing">
						{dragIcon || (
							<svg
								width={ICON_SIZE}
								height={ICON_SIZE}
								viewBox={`0 0 ${ICON_SIZE} ${ICON_SIZE}`}
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="text-current"
							>
								<circle cx={DRAG_HANDLE_DOT_X_LEFT} cy={DRAG_HANDLE_DOT_Y_TOP} r={DRAG_HANDLE_DOT_RADIUS} fill="currentColor" />
								<circle cx={DRAG_HANDLE_DOT_X_LEFT} cy={DRAG_HANDLE_DOT_Y_MIDDLE} r={DRAG_HANDLE_DOT_RADIUS} fill="currentColor" />
								<circle cx={DRAG_HANDLE_DOT_X_LEFT} cy={DRAG_HANDLE_DOT_Y_BOTTOM} r={DRAG_HANDLE_DOT_RADIUS} fill="currentColor" />
								<circle cx={DRAG_HANDLE_DOT_X_RIGHT} cy={DRAG_HANDLE_DOT_Y_TOP} r={DRAG_HANDLE_DOT_RADIUS} fill="currentColor" />
								<circle cx={DRAG_HANDLE_DOT_X_RIGHT} cy={DRAG_HANDLE_DOT_Y_MIDDLE} r={DRAG_HANDLE_DOT_RADIUS} fill="currentColor" />
								<circle cx={DRAG_HANDLE_DOT_X_RIGHT} cy={DRAG_HANDLE_DOT_Y_BOTTOM} r={DRAG_HANDLE_DOT_RADIUS} fill="currentColor" />
							</svg>
						)}
					</div>
				)}
				<div className="flex-1 min-w-0 text-sm font-medium text-primary truncate">{children}</div>
			</div>

			{!isDisabled && (showArrows || showDelete) && (
				<div className="flex items-center gap-2 shrink-0">
					{showArrows && (
						<div className="flex items-center gap-1">
							<button
								onClick={(e) => {
									e.stopPropagation();
									onMoveUp?.();
								}}
								disabled={!canMoveUp}
								className={cn(
									'p-1 rounded-sm transition-colors',
									'hover:bg-bgTertiary active:bg-bgTertiary',
									'disabled:cursor-not-allowed'
								)}
								style={{
									opacity: !canMoveUp ? DISABLED_BUTTON_OPACITY : undefined,
								}}
								aria-label="Move up"
								type="button"
							>
								{moveUpIcon || (
									<svg
										width={ICON_SIZE}
										height={ICON_SIZE}
										viewBox={`0 0 ${ICON_SIZE_LARGE} ${ICON_SIZE_LARGE}`}
										fill="none"
										stroke="currentColor"
										strokeWidth={ICON_STROKE_WIDTH}
										strokeLinecap="round"
										strokeLinejoin="round"
										className="text-tertiary"
									>
										<polyline points={ARROW_UP_POINTS}></polyline>
									</svg>
								)}
							</button>
							<button
								onClick={(e) => {
									e.stopPropagation();
									onMoveDown?.();
								}}
								disabled={!canMoveDown}
								className={cn(
									'p-1 rounded-sm transition-colors',
									'hover:bg-bgTertiary active:bg-bgTertiary',
									'disabled:cursor-not-allowed'
								)}
								style={{
									opacity: !canMoveDown ? DISABLED_BUTTON_OPACITY : undefined,
								}}
								aria-label="Move down"
								type="button"
							>
								{moveDownIcon || (
									<svg
										width={ICON_SIZE}
										height={ICON_SIZE}
										viewBox={`0 0 ${ICON_SIZE_LARGE} ${ICON_SIZE_LARGE}`}
										fill="none"
										stroke="currentColor"
										strokeWidth={ICON_STROKE_WIDTH}
										strokeLinecap="round"
										strokeLinejoin="round"
										className="text-tertiary"
									>
										<polyline points={ARROW_DOWN_POINTS}></polyline>
									</svg>
								)}
							</button>
						</div>
					)}
					{showDelete && (
						<button
							onClick={(e) => {
								e.stopPropagation();
								onDelete?.();
							}}
							className="p-1 rounded-sm transition-colors hover:bg-errorLight hover:text-error"
							aria-label="Remove"
							type="button"
						>
							{deleteIcon || (
								<svg
									width={ICON_SIZE}
									height={ICON_SIZE}
									viewBox={`0 0 ${ICON_SIZE_LARGE} ${ICON_SIZE_LARGE}`}
									fill="none"
									stroke="currentColor"
									strokeWidth={ICON_STROKE_WIDTH}
									strokeLinecap="round"
									strokeLinejoin="round"
									className="text-tertiary hover:text-error"
								>
									<polyline points={DELETE_LINE_POINTS}></polyline>
									<path d={DELETE_PATH_D}></path>
								</svg>
							)}
						</button>
					)}
				</div>
			)}
		</div>
	);
};

DraggableListItem.displayName = 'DraggableListItem';

